import { siteConfig } from "@acme/config";
import { NotFound } from "@acme/ui/not-found";
import { CACHE_TAGS } from "@acme/utils/cache-tags";
import {
  createFileRoute,
  ErrorComponent,
  notFound,
} from "@tanstack/react-router";
import {
  findSlugLocale,
  ProjectView,
} from "~/components/projects/project-view";
import { extractBlogFaqItems } from "~/lib/blog-faq";
import { taggedCacheHeaders } from "~/lib/cache-headers";
import { seo } from "~/lib/seo";
import { $getAllPublicProjects, $getProjectBySlug } from "~/lib/server/project";
import {
  generateStructuredDataGraph,
  getProjectSchemas,
} from "~/lib/structured-data";
import * as m from "~/paraglide/messages.js";
import { getLocale } from "~/paraglide/runtime.js";

export const Route = createFileRoute("/(public)/projects/$projectId")({
  loader: async ({ params: { projectId } }) => {
    try {
      const locale = getLocale();
      // The listing query is already cached for the index; reusing it keeps
      // "more to explore" to zero extra round trips.
      const [project, all] = await Promise.all([
        $getProjectBySlug({ data: { slug: projectId, locale } }),
        $getAllPublicProjects({ data: { locale } }),
      ]);
      return {
        project,
        related: all.filter((other) => other.id !== project.id).slice(0, 2),
      };
    } catch (error) {
      if (
        error instanceof Error &&
        (error.message === "Project not found" ||
          error.message === "Project is not public")
      ) {
        throw notFound();
      }
      console.error("[loader] $getProjectBySlug failed:", error);
      throw error;
    }
  },
  headers: taggedCacheHeaders(CACHE_TAGS.projects),
  head: ({ loaderData }) => {
    // See the matching branch in blog/$articleId.tsx — `head` runs after a
    // `notFound()` throw with no loader data, so the 404 would otherwise
    // canonicalise to `/projects/undefined` and ask to be indexed.
    if (!loaderData?.project) {
      const notFoundSeo = seo({
        title: `${m.project_not_found_title()} | ${siteConfig.title}`,
        path: "/projects",
        noindex: true,
      });
      return {
        meta: notFoundSeo.meta,
        links: notFoundSeo.links,
        scripts: [],
      };
    }

    const localeSlugs = loaderData?.project?.localeSlugs ?? {};
    const canonicalLocale = findSlugLocale(
      localeSlugs,
      loaderData?.project?.slug
    );
    const localePaths = Object.fromEntries(
      Object.entries(localeSlugs).map(([loc, slug]) => [
        loc,
        `/projects/${slug}`,
      ])
    );
    const canonicalPath =
      localePaths[canonicalLocale] ?? `/projects/${loaderData?.project?.slug}`;
    const seoData = seo({
      title: `${loaderData?.project?.title} | ${siteConfig.title}`,
      description: loaderData?.project?.description,
      keywords: siteConfig.keywords,
      image: loaderData?.project?.imageUrl,
      path: canonicalPath,
      localePaths,
      canonicalLocale,
    });

    const structuredData = loaderData?.project?.title
      ? generateStructuredDataGraph(
          getProjectSchemas({
            title: loaderData.project.title,
            description: loaderData.project.description || "",
            slug: loaderData.project.slug || "",
            // De-localized path plus the locale the slug resolved in: an
            // untranslated piece served under /lv/ is English content with
            // an English canonical, and its schema has to agree.
            locale: canonicalLocale,
            path: canonicalPath,
            image: loaderData.project.imageUrl ?? undefined,
            githubUrl: loaderData.project.githubUrl ?? undefined,
            stacks: loaderData.project.stacks ?? undefined,
            status: loaderData.project.status,
            dateCreated: loaderData.project.createdAt?.toISOString(),
            dateModified: loaderData.project.updatedAt?.toISOString(),
            faqItems: extractBlogFaqItems(loaderData.project.content),
          })
        )
      : null;

    return {
      meta: seoData.meta,
      links: seoData.links,
      scripts: structuredData
        ? [
            {
              type: "application/ld+json",
              children: structuredData,
            },
          ]
        : [],
    };
  },
  component: RouteComponent,
  errorComponent: ({ error }) => <ErrorComponent error={error} />,
  notFoundComponent: () => <NotFound>{m.project_not_found()}</NotFound>,
});

function RouteComponent() {
  const { project, related } = Route.useLoaderData();
  if (!project) {
    return null;
  }

  return <ProjectView project={project} related={related} />;
}
